import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Paper, Typography, useTheme } from "@mui/material/";
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import useLogout from "../../hooks/useLogout";
import { getColumnsModel, getConfigOptions, mapObjectToLabels } from "../../pages/Statistics/utils";
import {
  buildCrashListGridColumns,
  isValidatedMasterRow,
  splitCrashListRowsByValidation,
} from "./crash-list-merge-provenance.helper";
import {
  buildCrashListTabCountsQueryParams,
  buildCrashListViewQueryParams,
  crashListTabsShareValidatedDataset,
  CRASH_LIST_VIEW,
  getDefaultCrashListDatasetFilter,
  shouldLockCrashListDatasetToMerged,
  shouldShowCrashListCategoryDropdown,
} from "../../pages/Statistics/statistics-dataset-context";
import { StatsDatasetControls } from "./StatsDatasetControls";
import { GridToolbarContainer, useGridApiRef, getGridStringOperators, getGridDateOperators } from "@mui/x-data-grid";
import {
  buildValidatedDisplayRows,
  buildValidatedSourceChildRows,
  isMergeSourceChildRow,
} from "./crash-list-nested-rows.helper";
import { buildValidatedExpandColumn } from "./crash-list-validated-expand-column";
import { buildValidatedSourceRowsUrl } from "./crash-list-validated-sources.api";
import { exportCrashListCsv, getCrashListExportFilename } from "./crash-list-export.helper";
import useHttp from "../../hooks/useHttp";
import StatisticsFilterPanel from "./StatisticsFilterPanel";
import useHttpApiNew from "../../hooks/useHttpApiNew";
import { useConfigLocale } from "../../hooks/use-config-locale";
import { StripedDataGrid } from "../tables/StripedDataGrid";
import { NewApiBaseUrl } from "../../consts/new-api-base-url";
import { NAV_LAYOUT_MARGIN_START } from "../../consts/nav-layout-margin";
import {
  appendCrashListQueryParams,
  appendCrashListSortParams,
  countDistinctCrashesInPage,
  CRASH_LIST_DEFAULT_PAGE_SIZE,
  CRASH_LIST_DEFAULT_SORT_MODEL,
  CRASH_LIST_PAGE_SIZE_OPTIONS,
  estimateCrashListRowCount,
  getCrashListLimitOffset,
  getCrashListSortableFieldSet,
  normalizeCrashListPanelFilters,
  parseCrashCountResponse,
  parseCrashListTabCounts,
  parseCrashListTotalCount,
  resolveCrashListQueryFilters,
  resolveCrashListSortModel,
} from "../../utils/crash-list-pagination";

const newGetAllCrashesUrl = new URL(`${NewApiBaseUrl}/crash/all`);
const newCrashCountUrl = new URL(`${NewApiBaseUrl}/crash/count`);

const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: CRASH_LIST_DEFAULT_PAGE_SIZE };

function createEmptyCrashListViewCache(pageSize = CRASH_LIST_DEFAULT_PAGE_SIZE) {
  return {
    crashes: [],
    total: 0,
    page: 0,
    pageSize,
    sortModel: [...CRASH_LIST_DEFAULT_SORT_MODEL],
    panelFilters: null,
    filterModel: { items: [] },
    loaded: false,
  };
}

const CRASH_LIST_STRING_FILTER_OPS = new Set(['contains', 'equals', 'startsWith', 'endsWith']);
const CRASH_LIST_DATE_FILTER_OPS = new Set(['is', 'after', 'onOrAfter', 'before', 'onOrBefore']);

const crashListStringFilterOperators = getGridStringOperators().filter((op) =>
  CRASH_LIST_STRING_FILTER_OPS.has(op.value),
);
const crashListDateFilterOperators = getGridDateOperators().filter((op) =>
  CRASH_LIST_DATE_FILTER_OPS.has(op.value),
);

function buildPreparedRows(crashes, configuration, organizations) {
  if (!Array.isArray(crashes) || crashes.length === 0) return []
  const configOptions = getConfigOptions(configuration, organizations)
  const mappedCrashes = crashes.map((crash) => mapObjectToLabels(crash, configOptions))
  return mappedCrashes.map((crash, i) => {
    const source = crashes[i]
    const gridId = source?.id ?? i + 1
    const matchId = source?.crash?.match_id ?? source?.match_id ?? crash?.crash?.match_id ?? crash?.match_id ?? ''
    const mergedFromRaw = source?.merged_from_basics ?? source?.crash?.merged_from_basics ?? []
    const mergedFromBasics = Array.isArray(mergedFromRaw)
      ? mergedFromRaw.map((v) => Number(v)).filter((n) => n === 1 || n === 2 || n === 3)
      : []
    const mergeSourceCountRaw =
      source?.merge_source_count ?? source?.crash?.merge_source_count ?? null
    const mergeSourceCount = mergeSourceCountRaw == null
      ? null
      : Number(mergeSourceCountRaw)
    const mergedToCrashIdRaw =
      source?.merged_to_crash_id ?? source?.crash?.merged_to_crash_id ?? ''
    const mergedToCrashId =
      mergedToCrashIdRaw == null ? '' : String(mergedToCrashIdRaw).trim()
    const mergedToBasicRaw = source?.merged_to_basic ?? source?.crash?.merged_to_basic
    const mergedToBasicNum = Number(mergedToBasicRaw)
    const mergedToBasic =
      mergedToBasicNum === 1 || mergedToBasicNum === 2 || mergedToBasicNum === 3
        ? mergedToBasicNum
        : null
    const crashRowId = String(crash?.crash?.['Crash id'] ?? crash?.['Crash id'] ?? gridId)
    return {
      gridRowId: `${crashRowId}-${i}`,
      ...(crash.road ?? {}),
      ...(crash.unit ?? {}),
      ...(crash.unit?.people ?? {}),
      ...(crash.people ?? {}),
      ...(crash.crash ?? {}),
      ...crash,
      id: gridId,
      organizationValue: source?.crash?.organization ?? source?.organization,
      is_closed: source?.crash?.['is_closed'] ?? source?.['is_closed'],
      match_id: matchId == null ? '' : String(matchId),
      merge_date: source?.crash?.merge_date ?? source?.merge_date ?? crash?.crash?.merge_date ?? crash?.merge_date,
      merged_from_basics: mergedFromBasics,
      merge_source_count: Number.isFinite(mergeSourceCount) ? mergeSourceCount : null,
      merged_to_crash_id: mergedToCrashId,
      merged_to_basic: mergedToBasic,
    }
  })
}

const crashListToolbarContainerSx = (theme) => ({
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  gap: 1.5,
  px: 1.5,
  py: 0.75,
  minHeight: 44,
  alignItems: 'center',
  '& .MuiButton-root': {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 13,
    minHeight: 30,
    px: 1.25,
    textTransform: 'none',
    border: '1px solid rgba(255, 255, 255, 0.55)',
    borderRadius: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.22)',
      borderColor: '#ffffff',
    },
    '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
      color: theme.palette.common.white,
    },
  },
  '& .MuiFormControlLabel-label': {
    color: theme.palette.common.white,
    fontSize: 13,
    fontWeight: 500,
  },
  '& .crash-list-view-toggle .MuiToggleButton-root': {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: 12,
    minHeight: 32,
    px: 1.25,
    textTransform: 'none',
    borderColor: 'rgba(255, 255, 255, 0.45)',
    '&.Mui-selected': {
      color: '#0f172a',
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
      '&:hover': {
        backgroundColor: '#f1f5f9',
      },
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)',
      borderColor: 'rgba(255, 255, 255, 0.25)',
    },
  },
});

const CrashList = ({ isMobile, handleRowClick }) => {
  const { _ } = useLingui();
  const configLocale = useConfigLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const state = location.state;
  const token = state?.token || null;
  const init = useMemo(() => state?.init || {}, [state?.init]);
  const theme = useTheme();
  const { error } = useHttp();
  const [crashesLoading, setCrashesLoading] = useState(true);
  const [crashes, setCrashes] = useState([]);
  const [user, setUser] = useState({});
  const [rows, setRows] = useState([]);
  const [formModel, setFormModel] = useState({});
  const [formList, setFormList] = useState([]);
  const [message, setMessage] = useState('');
  const [pendingColumns, setPendingColumns] = useState([]);
  const [validatedColumns, setValidatedColumns] = useState([]);
  const [sourceColumns, setSourceColumns] = useState([]);
  const [pendingColumnGroupingModel, setPendingColumnGroupingModel] = useState([]);
  const [validatedColumnGroupingModel, setValidatedColumnGroupingModel] = useState([]);
  const [sourceColumnGroupingModel, setSourceColumnGroupingModel] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [expandedValidatedMasters, setExpandedValidatedMasters] = useState(
    () => new Set(),
  );
  const [validatedSourceRowsByMaster, setValidatedSourceRowsByMaster] = useState(
    () => new Map(),
  );
  const [loadingValidatedSources, setLoadingValidatedSources] = useState(
    () => new Set(),
  );
  const [validatedMastersWithoutSources, setValidatedMastersWithoutSources] = useState(
    () => new Set(),
  );
  const validatedSourcesAbortRef = useRef(/** @type {AbortController|null} */(null));
  const validatedSourcesCacheRef = useRef(/** @type {Map<string, object[]>} */(new Map()));
  const validatedSourcesLoadingRef = useRef(/** @type {Set<string>} */(new Set()));
  const expandedValidatedMastersRef = useRef(expandedValidatedMasters);
  const loadingValidatedSourcesRef = useRef(loadingValidatedSources);
  const validatedMastersWithoutSourcesRef = useRef(validatedMastersWithoutSources);
  expandedValidatedMastersRef.current = expandedValidatedMasters;
  loadingValidatedSourcesRef.current = loadingValidatedSources;
  validatedMastersWithoutSourcesRef.current = validatedMastersWithoutSources;
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [selectedCrashRow, setSelectedCrashRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION_MODEL);
  const [sortModel, setSortModel] = useState(
    /** @type {import('@mui/x-data-grid').GridSortModel} */([...CRASH_LIST_DEFAULT_SORT_MODEL]),
  );
  const [filterModel, setFilterModel] = useState(
    /** @type {import('@mui/x-data-grid').GridFilterModel} */({ items: [] }),
  );
  const [totalCrashCount, setTotalCrashCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState(/** @type {Record<string, string>|null} */null);
  const panelFiltersRef = useRef(/** @type {Record<string, string>|null} */(null));
  const lastFetchFiltersRef = useRef(/** @type {Record<string, string>|null} */(null));
  const [highlightedCrashId, setHighlightedCrashId] = useState(/** @type {string|null} */null);
  const [datasetFilter, setDatasetFilter] = useState(() =>
    getDefaultCrashListDatasetFilter(state?.init?.user, state?.init?.organizations),
  );
  const [crashListView, setCrashListView] = useState(() =>
    shouldLockCrashListDatasetToMerged(state?.init?.user, state?.init?.organizations)
      ? CRASH_LIST_VIEW.VALIDATED
      : CRASH_LIST_VIEW.PENDING,
  );
  // View the loaded rows belong to: prevents showing one tab's data under the other.
  const [crashesView, setCrashesView] = useState(CRASH_LIST_VIEW.PENDING);
  const [viewCounts, setViewCounts] = useState({ pending: 0, validated: 0, source: 0 });
  const [viewCache, setViewCache] = useState(() => ({
    [CRASH_LIST_VIEW.PENDING]: createEmptyCrashListViewCache(),
    [CRASH_LIST_VIEW.VALIDATED]: createEmptyCrashListViewCache(),
    [CRASH_LIST_VIEW.SOURCE]: createEmptyCrashListViewCache(),
  }));
  const crashListAbortRef = useRef(/** @type {AbortController|null} */(null));
  const viewCountsAbortRef = useRef(/** @type {AbortController|null} */(null));
  const crashListFetchSeqRef = useRef(0);
  const lockDatasetToMerged = useMemo(
    () => shouldLockCrashListDatasetToMerged(init?.user, init?.organizations),
    [init?.user, init?.organizations],
  );
  const pendingApiRef = useGridApiRef();
  const validatedApiRef = useGridApiRef();
  const sourceApiRef = useGridApiRef();
  const { request } = useHttpApiNew();
  const highlightCrashId = state?.highlightCrashId != null ? String(state.highlightCrashId) : null;

  const handleLockClick = useCallback((row) => {
    setSelectedCrashRow(row);
    setLockDialogOpen(true);
  }, []);
  const handleLockDialogClose = useCallback(() => {
    setLockDialogOpen(false);
    setSelectedCrashRow(null);
  }, []);
  const handleConfirmModify = useCallback(() => {
    if (selectedCrashRow && isValidatedMasterRow(selectedCrashRow)) {
      handleRowClick({ row: selectedCrashRow, fromLockConfirm: true });
      navigate('/home', { state: { token, init } });
    }
    handleLockDialogClose();
  }, [selectedCrashRow, handleLockDialogClose, handleRowClick, navigate, token, init]);

  const resolveTotalCrashCount = useCallback(async ({
    responseData,
    page,
    pageSize,
    pageList,
    datasetParams,
    filters,
    signal,
    isStale,
  }) => {
    const fromAll = parseCrashListTotalCount(responseData);
    if (fromAll != null) return fromAll;
    if (isStale?.()) return estimateCrashListRowCount({
      page,
      pageSize,
      pageDistinctCount: countDistinctCrashesInPage(pageList),
    });

    try {
      const countUrl = new URL(newCrashCountUrl);
      appendCrashListQueryParams(countUrl, { datasetParams, filters });
      const countRes = await request(
        countUrl,
        'GET',
        null,
        { Authorization: `Bearer ${token}` },
        { signal },
      );
      if (countRes?.aborted || isStale?.()) {
        return estimateCrashListRowCount({
          page,
          pageSize,
          pageDistinctCount: countDistinctCrashesInPage(pageList),
        });
      }
      if (!countRes?.responseAnyError) {
        const fromCount = parseCrashCountResponse(countRes.responseData);
        if (fromCount != null) return fromCount;
      }
    } catch {
      /* fallback below */
    }

    return estimateCrashListRowCount({
      page,
      pageSize,
      pageDistinctCount: countDistinctCrashesInPage(pageList),
    });
  }, [request, token]);

  const tabsShareValidatedDataset = useMemo(
    () => crashListTabsShareValidatedDataset({
      datasetFilter,
      lockDatasetToMerged,
    }),
    [datasetFilter, lockDatasetToMerged],
  );

  const resetValidatedExpandState = useCallback(() => {
    validatedSourcesAbortRef.current?.abort();
    validatedSourcesCacheRef.current = new Map();
    validatedSourcesLoadingRef.current = new Set();
    setExpandedValidatedMasters(new Set());
    setValidatedSourceRowsByMaster(new Map());
    setLoadingValidatedSources(new Set());
    setValidatedMastersWithoutSources(new Set());
  }, []);

  const invalidateViewCache = useCallback((pageSize = paginationModel.pageSize) => {
    resetValidatedExpandState();
    setViewCache({
      [CRASH_LIST_VIEW.PENDING]: createEmptyCrashListViewCache(pageSize),
      [CRASH_LIST_VIEW.VALIDATED]: createEmptyCrashListViewCache(pageSize),
      [CRASH_LIST_VIEW.SOURCE]: createEmptyCrashListViewCache(pageSize),
    });
  }, [paginationModel.pageSize, resetValidatedExpandState]);

  const showCategoryDropdown = useMemo(
    () => shouldShowCrashListCategoryDropdown(init?.user, init?.organizations ?? []),
    [init?.user, init?.organizations],
  );

  const restoreViewFromCache = useCallback((view) => {
    const cached = viewCache[view];
    if (!cached?.loaded) return false;
    setCrashes(cached.crashes);
    setCrashesView(view);
    setTotalCrashCount(cached.total);
    setPaginationModel({ page: cached.page, pageSize: cached.pageSize });
    setSortModel(resolveCrashListSortModel(cached.sortModel));
    if (cached.panelFilters !== undefined) {
      panelFiltersRef.current = cached.panelFilters;
      setActiveFilters(cached.panelFilters);
    }
    if (cached.filterModel !== undefined) {
      setFilterModel(cached.filterModel);
    }
    setCrashesLoading(false);
    return true;
  }, [viewCache]);

  const requestCrashCount = useCallback(async ({ datasetParams, filters, signal }) => {
    const countUrl = new URL(newCrashCountUrl);
    appendCrashListQueryParams(countUrl, { datasetParams, filters });

    const countRes = await request(
      countUrl,
      'GET',
      null,
      { Authorization: `Bearer ${token}` },
      { signal },
    );
    if (countRes?.aborted || countRes?.responseAnyError) return null;
    return countRes.responseData ?? null;
  }, [request, token]);

  /** Fallback for APIs without `merged=both`: count the hidden tab on its own. */
  const fetchHiddenViewCount = useCallback(async ({
    activeView,
    useDatasetFilter,
    filters,
    signal,
  }) => {
    const hiddenView = activeView === CRASH_LIST_VIEW.VALIDATED
      ? CRASH_LIST_VIEW.PENDING
      : CRASH_LIST_VIEW.VALIDATED;

    const responseData = await requestCrashCount({
      datasetParams: buildCrashListViewQueryParams({
        view: hiddenView,
        datasetFilter: useDatasetFilter,
        lockDatasetToMerged,
        user: init?.user,
      }),
      filters,
      signal,
    });

    const hiddenCount = parseCrashCountResponse(responseData);
    if (hiddenCount == null) return;

    const hiddenKey = hiddenView === CRASH_LIST_VIEW.VALIDATED ? 'validated' : 'pending';
    setViewCounts((prev) => ({ ...prev, [hiddenKey]: hiddenCount }));
  }, [requestCrashCount, lockDatasetToMerged, init?.user]);

  /**
   * Both tab badges from a single `/crash/count?merged=both` scan. The visible grid keeps
   * using the `totalCount` already returned by `/crash/all`, so this is the only extra call.
   * Analysts locked to the merged dataset see the same rows in both tabs — no call needed.
   */
  const fetchViewCounts = useCallback(async ({
    filters,
    filterModelOverride,
    useDatasetFilter = datasetFilter,
    activeView = crashListView,
    fetchSeq,
  }) => {
    if (crashListTabsShareValidatedDataset({
      datasetFilter: useDatasetFilter,
      lockDatasetToMerged,
    })) return;
    if (fetchSeq != null && fetchSeq !== crashListFetchSeqRef.current) return;

    viewCountsAbortRef.current?.abort();
    const controller = new AbortController();
    viewCountsAbortRef.current = controller;

    const countFilters = resolveCrashListQueryFilters(
      filters,
      filterModelOverride ?? filterModel,
      init?.configuration,
    );
    const isCurrent = () => !controller.signal.aborted
      && (fetchSeq == null || fetchSeq === crashListFetchSeqRef.current);

    try {
      const combined = await requestCrashCount({
        datasetParams: buildCrashListTabCountsQueryParams({
          datasetFilter: useDatasetFilter,
          lockDatasetToMerged,
          user: init?.user,
        }),
        filters: countFilters,
        signal: controller.signal,
      });
      if (!isCurrent()) return;

      const counts = parseCrashListTabCounts(combined);
      if (counts) {
        setViewCounts(counts);
        return;
      }

      await fetchHiddenViewCount({
        activeView,
        useDatasetFilter,
        filters: countFilters,
        signal: controller.signal,
      });
    } catch {
      /* pill counts are optional UX */
    }
  }, [
    filterModel,
    init?.configuration,
    init?.user,
    requestCrashCount,
    fetchHiddenViewCount,
    datasetFilter,
    crashListView,
    lockDatasetToMerged,
  ]);

  /**
   * Server-paginated fetch. Backend supports distinct-crash LIMIT/OFFSET,
   * totalCount, sort/order, and field filters (equal/from/to + contains* when available).
   * Falls back to /crash/count or estimate if totalCount is missing.
   */
  const getCrashes = useCallback(async (data, options = {}) => {
    const fetchSeq = ++crashListFetchSeqRef.current;
    const isStale = () => fetchSeq !== crashListFetchSeqRef.current;

    const nextPage = options.page ?? paginationModel.page;
    const nextPageSize = options.pageSize ?? paginationModel.pageSize;
    const nextSortModel = resolveCrashListSortModel(options.sortModel ?? sortModel);
    const nextFilterModel = options.filterModel ?? filterModel;
    const activeView = options.crashListView ?? crashListView;
    const baseDatasetFilter = options.datasetFilter ?? datasetFilter;
    const datasetParams = buildCrashListViewQueryParams({
      view: activeView,
      datasetFilter: baseDatasetFilter,
      lockDatasetToMerged,
      user: init?.user,
    });
    const panelFilters = data !== undefined ? data : activeFilters;
    const filters = resolveCrashListQueryFilters(
      panelFilters,
      nextFilterModel,
      init?.configuration,
    );
    const resetPage = options.resetPage === true;

    crashListAbortRef.current?.abort();
    viewCountsAbortRef.current?.abort();
    const controller = new AbortController();
    crashListAbortRef.current = controller;

    setCrashesLoading(true);
    try {
      const { limit, offset } = getCrashListLimitOffset(
        resetPage ? 0 : nextPage,
        nextPageSize,
      );
      const allCrashesUrl = new URL(newGetAllCrashesUrl);
      allCrashesUrl.searchParams.set('format', 'table');
      allCrashesUrl.searchParams.set('limit', String(limit));
      allCrashesUrl.searchParams.set('offset', String(offset));
      appendCrashListQueryParams(allCrashesUrl, { datasetParams, filters });
      appendCrashListSortParams(allCrashesUrl, nextSortModel, init?.configuration);

      const res = await request(
        allCrashesUrl,
        'GET',
        null,
        { Authorization: `Bearer ${token}` },
        { signal: controller.signal },
      );
      if (isStale() || res?.aborted || res?.responseAnyError) return;

      const list = res.responseData?.crashes ?? [];
      // Preserve server order (do not re-sort client-side).
      const pageCrashes = list.map((crash, i) => ({
        ...crash,
        id: i + 1,
        gridRowId: `${String(crash?.crash?.['Crash id'] ?? crash?.['Crash id'] ?? i + 1)}-${i}`,
      }));

      if (isStale()) return;

      setCrashes(pageCrashes);
      setCrashesView(activeView);
      lastFetchFiltersRef.current = filters;
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0, pageSize: nextPageSize }));
      }

      const total = await resolveTotalCrashCount({
        responseData: res.responseData,
        page: resetPage ? 0 : nextPage,
        pageSize: nextPageSize,
        pageList: pageCrashes,
        datasetParams,
        filters,
        signal: controller.signal,
        isStale,
      });
      if (isStale()) return;

      setTotalCrashCount(total);
      setViewCache((prev) => ({
        ...prev,
        [activeView]: {
          crashes: pageCrashes,
          total,
          page: resetPage ? 0 : nextPage,
          pageSize: nextPageSize,
          sortModel: nextSortModel,
          panelFilters: normalizeCrashListPanelFilters(panelFilters),
          filterModel: nextFilterModel,
          loaded: true,
        },
      }));

      const sharesValidatedDataset = crashListTabsShareValidatedDataset({
        datasetFilter: baseDatasetFilter,
        lockDatasetToMerged,
      });
      if (sharesValidatedDataset) {
        setViewCounts({ pending: total, validated: total, source: total });
      } else {
        const activeCountKey = activeView === CRASH_LIST_VIEW.VALIDATED
          ? 'validated'
          : activeView === CRASH_LIST_VIEW.SOURCE
            ? 'source'
            : 'pending';
        setViewCounts((prev) => ({ ...prev, [activeCountKey]: total }));
      }
      if (options.refreshViewCounts === true && !sharesValidatedDataset) {
        void fetchViewCounts({
          filters: panelFilters,
          filterModelOverride: nextFilterModel,
          useDatasetFilter: baseDatasetFilter,
          activeView,
          fetchSeq,
        });
      }
    } finally {
      if (!isStale() && !controller.signal.aborted) setCrashesLoading(false);
    }
  }, [
    token,
    request,
    datasetFilter,
    crashListView,
    lockDatasetToMerged,
    activeFilters,
    sortModel,
    filterModel,
    init?.configuration,
    paginationModel.page,
    paginationModel.pageSize,
    resolveTotalCrashCount,
    fetchViewCounts,
  ]);

  const handlePaginationModelChange = useCallback((model) => {
    const pageChanged = model.page !== paginationModel.page;
    const sizeChanged = model.pageSize !== paginationModel.pageSize;
    setPaginationModel(sizeChanged ? { ...model, page: 0 } : model);
    if (!pageChanged && !sizeChanged) return;
    getCrashes(activeFilters, {
      page: sizeChanged ? 0 : model.page,
      pageSize: model.pageSize,
      resetPage: sizeChanged,
    });
  }, [paginationModel.page, paginationModel.pageSize, getCrashes, activeFilters]);

  const handleSortModelChange = useCallback((model) => {
    // Clearing the sort falls back to the default instead of the backend ascending order.
    const nextSortModel = resolveCrashListSortModel(model);
    setSortModel(nextSortModel);
    getCrashes(activeFilters, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      sortModel: nextSortModel,
    });
  }, [getCrashes, activeFilters, paginationModel.pageSize]);

  const handleFilterModelChange = useCallback((model) => {
    setFilterModel(model);
    invalidateViewCache();
    getCrashes(activeFilters, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      filterModel: model,
      refreshViewCounts: true,
    });
  }, [getCrashes, activeFilters, paginationModel.pageSize, invalidateViewCache]);

  useEffect(() => {
    if (!token) return logout();
    if (isMobile) return navigate('/home', { state: { token, init } });
    if (!init?.configuration) return;

    getCrashes(null, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      refreshViewCounts: true,
    });

    return () => {
      crashListAbortRef.current?.abort();
      viewCountsAbortRef.current?.abort();
      validatedSourcesAbortRef.current?.abort();
    };
    // Initial load when configuration is ready — StrictMode re-runs after abort cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, logout, isMobile, navigate, init?.configuration]);

  const { pendingRows, validatedRows } = useMemo(
    () => splitCrashListRowsByValidation(rows),
    [rows],
  );

  // Ogni tab ha la sua query server: mostra le righe solo se appartengono alla tab attiva.
  const rowsMatchActiveView = crashesView === crashListView;

  const displayPendingRows = useMemo(() => {
    if (tabsShareValidatedDataset) return [];
    if (crashListView !== CRASH_LIST_VIEW.PENDING || !rowsMatchActiveView) return [];
    return rows;
  }, [tabsShareValidatedDataset, crashListView, rowsMatchActiveView, rows]);

  const validatedMasterRows = useMemo(() => {
    if (crashListView !== CRASH_LIST_VIEW.VALIDATED || !rowsMatchActiveView) return [];
    return rows.filter((row) => isValidatedMasterRow(row));
  }, [crashListView, rowsMatchActiveView, rows]);

  const displayValidatedRows = useMemo(() => {
    if (!validatedMasterRows.length) return [];
    return buildValidatedDisplayRows({
      masterRows: validatedMasterRows,
      expandedMasterIds: expandedValidatedMasters,
      sourcesByMaster: validatedSourceRowsByMaster,
    });
  }, [validatedMasterRows, expandedValidatedMasters, validatedSourceRowsByMaster]);

  const handleValidatedMasterExpandToggle = useCallback(async (masterCrashId) => {
    const masterId = String(masterCrashId ?? '').trim();
    if (!masterId) return;

    if (expandedValidatedMastersRef.current.has(masterId)) {
      setExpandedValidatedMasters((prev) => {
        const next = new Set(prev);
        next.delete(masterId);
        return next;
      });
      return;
    }

    if (validatedSourcesCacheRef.current.has(masterId)) {
      const cachedSources = validatedSourcesCacheRef.current.get(masterId) ?? [];
      if (cachedSources.length === 0) {
        setValidatedMastersWithoutSources((prev) => new Set(prev).add(masterId));
        return;
      }
      setExpandedValidatedMasters((prev) => new Set(prev).add(masterId));
      return;
    }
    if (validatedSourcesLoadingRef.current.has(masterId)) return;

    setExpandedValidatedMasters((prev) => new Set(prev).add(masterId));

    validatedSourcesAbortRef.current?.abort();
    const controller = new AbortController();
    validatedSourcesAbortRef.current = controller;
    validatedSourcesLoadingRef.current.add(masterId);

    setLoadingValidatedSources((prev) => new Set(prev).add(masterId));
    try {
      const sourceUrl = buildValidatedSourceRowsUrl({
        baseUrl: newGetAllCrashesUrl.toString(),
        masterCrashId: masterId,
        panelFilters: activeFilters,
        filterModel,
        configuration: init?.configuration,
        sortModel,
        datasetFilter,
        lockDatasetToMerged,
        user: init?.user,
      });
      const res = await request(
        sourceUrl,
        'GET',
        null,
        { Authorization: `Bearer ${token}` },
        { signal: controller.signal },
      );
      if (controller.signal.aborted || res?.aborted || res?.responseAnyError) return;

      const prepared = buildValidatedSourceChildRows(
        masterId,
        buildPreparedRows(
          res.responseData?.crashes ?? [],
          init?.configuration,
          init?.organizations ?? [],
        ),
      );
      validatedSourcesCacheRef.current = new Map(validatedSourcesCacheRef.current).set(
        masterId,
        prepared,
      );
      setValidatedSourceRowsByMaster((prev) => new Map(prev).set(masterId, prepared));
      if (prepared.length === 0) {
        setValidatedMastersWithoutSources((prev) => new Set(prev).add(masterId));
        setExpandedValidatedMasters((prev) => {
          if (!prev.has(masterId)) return prev;
          const next = new Set(prev);
          next.delete(masterId);
          return next;
        });
      }
    } finally {
      validatedSourcesLoadingRef.current.delete(masterId);
      setLoadingValidatedSources((prev) => {
        const next = new Set(prev);
        next.delete(masterId);
        return next;
      });
    }
  }, [
    activeFilters,
    filterModel,
    init?.configuration,
    init?.organizations,
    init?.user,
    sortModel,
    datasetFilter,
    lockDatasetToMerged,
    request,
    token,
  ]);

  const displaySourceRows = useMemo(() => {
    if (tabsShareValidatedDataset) return [];
    if (crashListView !== CRASH_LIST_VIEW.SOURCE || !rowsMatchActiveView) return [];
    return rows;
  }, [tabsShareValidatedDataset, crashListView, rowsMatchActiveView, rows]);

  const pendingTabCount = viewCounts.pending;
  const validatedTabCount = viewCounts.validated;
  const sourceTabCount = viewCounts.source;

  const handleCrashListViewChange = useCallback((_event, value) => {
    if (value == null || value === crashListView) return;
    setCrashListView(value);
    resetValidatedExpandState();

    if (restoreViewFromCache(value)) return;

    // Drop the previous tab's rows: stale data under a different view is misleading.
    setCrashes([]);
    setTotalCrashCount(0);
    setCrashesView(value);

    // Hidden-tab count already matches the current filters: fetch rows only.
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    getCrashes(activeFilters, {
      crashListView: value,
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
    });
  }, [
    crashListView,
    restoreViewFromCache,
    getCrashes,
    activeFilters,
    paginationModel.pageSize,
    resetValidatedExpandState,
  ]);

  useEffect(() => {
    if (!highlightCrashId || crashesLoading || !rows.length) return;

    const isValidatedHighlight = validatedRows.some(
      (row) => String(row['Crash id']) === highlightCrashId,
    );
    if (isValidatedHighlight) {
      setCrashListView(CRASH_LIST_VIEW.VALIDATED);
    }

    const rowIndex = rows.findIndex(
      (row) => String(row['Crash id']) === highlightCrashId
    );
    if (rowIndex < 0) {
      // Server pagination: crash may be on another page — highlight only if present here.
      navigate('.', {
        replace: true,
        state: { ...(state || {}), highlightCrashId: undefined }
      });
      return;
    }

    setHighlightedCrashId(highlightCrashId);

    navigate('.', {
      replace: true,
      state: { ...(state || {}), highlightCrashId: undefined }
    });
  }, [highlightCrashId, crashesLoading, rows, validatedRows, navigate, state]);

  useEffect(() => {
    if (!highlightedCrashId || !rows.length) return;

    const validatedIndex = validatedRows.findIndex(
      (row) => String(row['Crash id']) === highlightedCrashId,
    );
    const pendingIndex = pendingRows.findIndex(
      (row) => String(row['Crash id']) === highlightedCrashId,
    );
    if (validatedIndex < 0 && pendingIndex < 0) return;

    const scrollTimer = window.setTimeout(() => {
      if (validatedIndex >= 0) {
        validatedApiRef.current?.scrollToIndexes({ rowIndex: validatedIndex });
        return;
      }
      pendingApiRef.current?.scrollToIndexes({ rowIndex: pendingIndex });
    }, 200);

    return () => window.clearTimeout(scrollTimer);
  }, [highlightedCrashId, rows, validatedRows, pendingRows, paginationModel.page, pendingApiRef, validatedApiRef]);

  useEffect(() => {
    const { configuration, organizations, user } = init;
    setUser(user);
    const preparedRows = buildPreparedRows(crashes, configuration, organizations);
    setRows(preparedRows);
    setFormModel(configuration);
    const initForms = Object.keys(configuration).sort((a, b) => configuration[a].position - configuration[b].position);
    setFormList(initForms);
    const sortableFields = getCrashListSortableFieldSet(configuration);
    const isAdmin = init?.user?.type === 1;
    const columnOptions = {
      configuration,
      translate: _,
      sortableFields,
      dateFilterOperators: crashListDateFilterOperators,
      stringFilterOperators: crashListStringFilterOperators,
      isAdmin,
      onLockClick: handleLockClick,
      getColumnsModel,
    };
    const pendingGrid = buildCrashListGridColumns({
      ...columnOptions,
      mergeColumnMode: 'none',
      showValidatedLocks: false,
    });
    const validatedGrid = buildCrashListGridColumns({
      ...columnOptions,
      mergeColumnMode: 'none',
      showValidatedLocks: true,
    });
    const expandColumn = buildValidatedExpandColumn({
      translate: _,
      isMasterExpanded: (masterId) => expandedValidatedMastersRef.current.has(masterId),
      isMasterLoading: (masterId) => loadingValidatedSourcesRef.current.has(masterId),
      isMasterExpandDisabled: (masterId) => validatedMastersWithoutSourcesRef.current.has(masterId),
      onToggleExpand: handleValidatedMasterExpandToggle,
    });
    const sourceGrid = buildCrashListGridColumns({
      ...columnOptions,
      mergeColumnMode: 'merged-to',
      showValidatedLocks: false,
    });
    setPendingColumns(pendingGrid.columns);
    setPendingColumnGroupingModel(pendingGrid.columnGroupingModel);
    setValidatedColumns([expandColumn, ...validatedGrid.columns]);
    setValidatedColumnGroupingModel(validatedGrid.columnGroupingModel);
    setSourceColumns(sourceGrid.columns);
    setSourceColumnGroupingModel(sourceGrid.columnGroupingModel);
  }, [
    init,
    crashes,
    handleLockClick,
    configLocale,
    _,
    handleValidatedMasterExpandToggle,
    validatedMastersWithoutSources,
  ]);

  useEffect(() => {
    if (totalCrashCount <= 0) return;
    const maxPage = Math.max(0, Math.ceil(totalCrashCount / paginationModel.pageSize) - 1);
    if (paginationModel.page <= maxPage) return;
    setPaginationModel((prev) => ({ ...prev, page: maxPage }));
    getCrashes(activeFilters, { page: maxPage, pageSize: paginationModel.pageSize });
  }, [
    totalCrashCount,
    paginationModel.page,
    paginationModel.pageSize,
    activeFilters,
    getCrashes,
  ]);



  const buildGroupedRows = useCallback((tableRows) => {
    if (!tableRows?.length) {
      return {};
    }
    const groupBy = Object.groupBy(tableRows, (item) => item?.['Crash id']);
    const toArray = Object.entries(groupBy || {})?.map(([crashId, groupRows], index) => [crashId, index % 2 === 0]);
    return toArray.reduce((acc, [crashId, isEven]) => ({ ...acc, [crashId]: isEven }), {});
  }, []);

  const pendingGroupedRows = useMemo(
    () => buildGroupedRows(displayPendingRows),
    [displayPendingRows, buildGroupedRows],
  );

  const validatedGroupedRows = useMemo(
    () => buildGroupedRows(displayValidatedRows.filter((row) => !isMergeSourceChildRow(row))),
    [displayValidatedRows, buildGroupedRows],
  );

  const sourceGroupedRows = useMemo(
    () => buildGroupedRows(displaySourceRows),
    [displaySourceRows, buildGroupedRows],
  );

  const handleExportClick = useCallback(async () => {
    setExportLoading(true);
    try {
      const activeColumns = crashListView === CRASH_LIST_VIEW.VALIDATED
        ? validatedColumns
        : crashListView === CRASH_LIST_VIEW.SOURCE
          ? sourceColumns
          : pendingColumns;
      const datasetParams = buildCrashListViewQueryParams({
        view: crashListView,
        datasetFilter,
        lockDatasetToMerged,
        user: init?.user,
      });
      await exportCrashListCsv({
        baseUrl: newGetAllCrashesUrl.toString(),
        token,
        request,
        datasetParams,
        panelFilters: panelFiltersRef.current ?? activeFilters,
        queryFilters: lastFetchFiltersRef.current,
        filterModel,
        configuration: init?.configuration,
        sortModel,
        columns: activeColumns,
        view: crashListView,
        prepareRows: (list) => buildPreparedRows(list, init?.configuration, init?.organizations ?? []),
        filename: getCrashListExportFilename(crashListView),
      });
    } finally {
      setExportLoading(false);
    }
  }, [
    crashListView,
    validatedColumns,
    sourceColumns,
    pendingColumns,
    datasetFilter,
    lockDatasetToMerged,
    init?.user,
    init?.configuration,
    init?.organizations,
    token,
    request,
    activeFilters,
    filterModel,
    sortModel,
  ]);

  const handleFilterSubmit = (searchParams) => {
    const nextPanelFilters = normalizeCrashListPanelFilters(searchParams);
    panelFiltersRef.current = nextPanelFilters;
    setActiveFilters(nextPanelFilters);
    invalidateViewCache();
    getCrashes(nextPanelFilters, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      refreshViewCounts: true,
    });
  };

  const handleFilterReset = () => {
    setFilterModel({ items: [] });
    panelFiltersRef.current = null;
    lastFetchFiltersRef.current = null;
    setActiveFilters(null);
    invalidateViewCache();
    getCrashes(null, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      filterModel: { items: [] },
      refreshViewCounts: true,
    });
  };

  const TopToolbar = () => (
    <GridToolbarContainer sx={crashListToolbarContainerSx(theme)}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Button
          size="small"
          disabled={exportLoading || crashesLoading}
          onClick={handleExportClick}
        >
          {exportLoading ? _(msg`Exporting…`) : _(msg`Export`)}
        </Button>
        <StatsDatasetControls
          user={init.user}
          organizations={init.organizations ?? []}
          reportType={0}
          basicOrganization={datasetFilter}
          onBasicOrganizationChange={(value) => {
            if (String(value ?? '') === String(datasetFilter ?? '')) return;
            setDatasetFilter(value);
            invalidateViewCache();
            getCrashes(activeFilters, {
              datasetFilter: value,
              page: 0,
              pageSize: paginationModel.pageSize,
              resetPage: true,
              refreshViewCounts: true,
            });
          }}
          showReportType={false}
          showBasicOrganization={showCategoryDropdown && !lockDatasetToMerged}
          variant="toolbar"
          isDark={theme.palette.mode === 'dark'}
          theme={theme}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          className="crash-list-view-toggle"
          value={crashListView}
          disabled={crashesLoading}
          onChange={handleCrashListViewChange}
          sx={{ flexWrap: 'wrap' }}
        >
          {!tabsShareValidatedDataset && (
            <ToggleButton value={CRASH_LIST_VIEW.PENDING}>
              {`${_(msg`Crashes to validate`)} (${pendingTabCount})`}
            </ToggleButton>
          )}
          <ToggleButton value={CRASH_LIST_VIEW.VALIDATED}>
            {`${_(msg`Validated crashes`)} (${validatedTabCount})`}
          </ToggleButton>
          {!tabsShareValidatedDataset && (
            <ToggleButton value={CRASH_LIST_VIEW.SOURCE}>
              {`${_(msg`Source crashes`)} (${sourceTabCount})`}
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>
    </GridToolbarContainer>
  );


  return (
    <>
            <Box
        pl={2}
        pr={2}
        display='flex'
        flexDirection='column'
        alignItems='stretch'
        sx={{
          marginInlineStart: NAV_LAYOUT_MARGIN_START,
          '& .overload-theme--header': {
            backgroundColor: theme.mode === 'light' && theme.palette.primary.main,
            color: theme.mode === 'light' && theme.palette.primary.contrastText
          }
        }}>
        
                {formList.length > 0 &&
        <StatisticsFilterPanel
          newLabel={_(msg`Crash list`)}
          title={_(msg`Crashes`)}
          formList={formList}
          filterDisabled={crashesLoading}
          onFilterSubmit={handleFilterSubmit}
          onFilterReset={handleFilterReset}
          user={user}
          formModel={formModel}
          isMobile={isMobile}
          collapsible
          defaultExpanded={false} />

        }
                {formList.length > 0 &&
        <Box sx={{ mb: 2 }}>
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '50vh',
              position: 'relative',
            }}>
            {crashListView === CRASH_LIST_VIEW.PENDING && (
              <StripedDataGrid
                key={CRASH_LIST_VIEW.PENDING}
                apiRef={pendingApiRef}
                loading={crashesLoading}
                showColumnVerticalBorder
                showCellVerticalBorder
                unstable_rowSpanning={!crashesLoading}
                getRowId={(row) => String(row.gridRowId ?? row.id)}
                rows={displayPendingRows}
                onRowClick={(params) => {
                  handleRowClick(params);
                  navigate('/home', { state: { token, init } });
                }}
                columns={pendingColumns}
                columnGroupingModel={pendingColumnGroupingModel}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                filterDebounceMs={400}
                rowCount={totalCrashCount}
                initialState={{
                  columns: { columnVisibilityModel: { id: false } },
                }}
                pageSizeOptions={CRASH_LIST_PAGE_SIZE_OPTIONS}
                getRowClassName={(params) => {
                  const stripe = pendingGroupedRows?.[params?.row?.['Crash id']] ? 'even' : 'odd';
                  const highlighted =
                    highlightedCrashId &&
                    String(params?.row?.['Crash id']) === highlightedCrashId
                      ? 'highlighted-crash-row'
                      : '';
                  return [stripe, highlighted].filter(Boolean).join(' ');
                }}
                sx={{
                  '& .MuiDataGrid-row.highlighted-crash-row': {
                    boxShadow: `inset 4px 0 0 ${theme.palette.newColors?.yellow || '#E0D028'}`,
                    backgroundColor: 'rgba(246, 213, 82, 0.35)',
                  },
                }}
                slots={{ toolbar: TopToolbar }}
              />
            )}
            {crashListView === CRASH_LIST_VIEW.VALIDATED && (
              <StripedDataGrid
                key={CRASH_LIST_VIEW.VALIDATED}
                apiRef={validatedApiRef}
                loading={crashesLoading}
                showColumnVerticalBorder
                showCellVerticalBorder
                unstable_rowSpanning={!crashesLoading}
                getRowId={(row) => String(row.gridRowId ?? row.id)}
                rows={displayValidatedRows}
                onRowClick={(params) => {
                  if (isMergeSourceChildRow(params.row)) return;
                  handleRowClick(params);
                  navigate('/home', { state: { token, init } });
                }}
                columns={validatedColumns}
                columnGroupingModel={validatedColumnGroupingModel}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                filterDebounceMs={400}
                rowCount={totalCrashCount}
                initialState={{
                  columns: { columnVisibilityModel: { id: false } },
                }}
                pageSizeOptions={CRASH_LIST_PAGE_SIZE_OPTIONS}
                getRowClassName={(params) => {
                  const row = params.row;
                  const stripeCrashId = isMergeSourceChildRow(row)
                    ? String(row.merged_to_crash_id || row.match_id || '')
                    : String(row['Crash id'] ?? '');
                  const stripe = validatedGroupedRows?.[stripeCrashId] ? 'even' : 'odd';
                  const childClass = isMergeSourceChildRow(row) ? 'merge-source-child-row' : '';
                  const highlighted =
                    highlightedCrashId &&
                    String(row['Crash id']) === highlightedCrashId
                      ? 'highlighted-crash-row'
                      : '';
                  return [stripe, childClass, highlighted].filter(Boolean).join(' ');
                }}
                sx={{
                  border: 0,
                  '& .MuiDataGrid-row.merge-source-child-row .MuiDataGrid-cell': {
                    pl: 3,
                    fontSize: 12,
                    color: 'text.secondary',
                  },
                  '& .MuiDataGrid-row.highlighted-crash-row': {
                    boxShadow: `inset 4px 0 0 ${theme.palette.newColors?.yellow || '#E0D028'}`,
                    backgroundColor: 'rgba(246, 213, 82, 0.35)',
                  },
                }}
                slots={{ toolbar: TopToolbar }}
              />
            )}
            {crashListView === CRASH_LIST_VIEW.SOURCE && (
              <StripedDataGrid
                key={CRASH_LIST_VIEW.SOURCE}
                apiRef={sourceApiRef}
                loading={crashesLoading}
                showColumnVerticalBorder
                showCellVerticalBorder
                unstable_rowSpanning={!crashesLoading}
                getRowId={(row) => String(row.gridRowId ?? row.id)}
                rows={displaySourceRows}
                onRowClick={(params) => {
                  handleRowClick(params);
                  navigate('/home', { state: { token, init } });
                }}
                columns={sourceColumns}
                columnGroupingModel={sourceColumnGroupingModel}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                filterDebounceMs={400}
                rowCount={totalCrashCount}
                initialState={{
                  columns: { columnVisibilityModel: { id: false } },
                }}
                pageSizeOptions={CRASH_LIST_PAGE_SIZE_OPTIONS}
                getRowClassName={(params) => {
                  const stripe = sourceGroupedRows?.[params?.row?.['Crash id']] ? 'even' : 'odd';
                  const highlighted =
                    highlightedCrashId &&
                    String(params?.row?.['Crash id']) === highlightedCrashId
                      ? 'highlighted-crash-row'
                      : '';
                  return [stripe, highlighted].filter(Boolean).join(' ');
                }}
                sx={{
                  '& .MuiDataGrid-cell.merged-to-cell': {
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-cell.merged-to-cell .MuiDataGrid-cellContent': {
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'visible',
                  },
                  '& .MuiDataGrid-row.highlighted-crash-row': {
                    boxShadow: `inset 4px 0 0 ${theme.palette.newColors?.yellow || '#E0D028'}`,
                    backgroundColor: 'rgba(246, 213, 82, 0.35)',
                  },
                }}
                slots={{ toolbar: TopToolbar }}
              />
            )}
          </Paper>
        </Box>
        }
            </Box>
            {error && <Alert sx={{ mt: 2 }} severity="error">
                <Trans>There was an error: {error}.</Trans>
            </Alert>}
            {message && <Alert sx={{ mt: 2 }} severity='success'>
                {message}
            </Alert>}
            <Dialog open={lockDialogOpen} onClose={handleLockDialogClose}>
                <DialogTitle sx={{ color: theme.palette.newColors.gray }}>
                    <Trans>Modify validated crash</Trans>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Trans>This crash is already recorded in the national database. Do you want to proceed with the modification?</Trans>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="error" onClick={handleLockDialogClose}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
            variant="outlined"
            sx={{ color: theme.palette.newColors.gray, fontWeight: 600 }}
            onClick={handleConfirmModify}
            autoFocus>
            
                        <Trans>Confirm</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        </>);

};

export default CrashList;